import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ACTIONS } from "src/_config";
import { useChatApi } from "src/_common/hooks/actions/chat/appChatApiHook";
import { subscribeToConversation } from "src/_common/hooks/usePusher";
import { ChatMessageItem } from "src/store/reducers/chat/chatReducer";
import MessageBubble from "./MessageBubble";

const ALLOWED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

interface Props {
  conversationId: string;
  isBlocked: boolean;
  onBlockToggle: () => void;
  compact?: boolean;
}

const ChatWindow: React.FC<Props> = ({ conversationId, isBlocked, onBlockToggle, compact = false }) => {
  const dispatch = useDispatch();
  const { callGetMessages, callSendMessage, callMarkAsRead } = useChatApi();

  const messages: ChatMessageItem[] = useSelector(
    (state: any) => state.chat.messages[conversationId] || []
  );

  // Get conversation data to show jobseeker name
  const conversation = useSelector((state: any) =>
    state.chat.conversations?.find((c: any) => c.conversation_id === conversationId)
  );

  const jobseekerName = conversation?.jobseeker_name || "Conversation";

  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState("");
  const [sending, setSending] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const LIMIT = 30;

  const bottomRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Load initial messages
  useEffect(() => {
    if (!conversationId) return;
    setLoading(true);
    callGetMessages(
      conversationId,
      { limit: LIMIT, offset: 0 },
      (_msg: string, data: any) => {
        const msgs = data?.messages || [];
        const total = data?.total || 0;
        dispatch({ type: ACTIONS.CHAT.SET_MESSAGES, payload: { conversationId, messages: msgs } });
        setOffset(msgs.length);
        setHasMore(msgs.length < total);
        setLoading(false);
      },
      () => setLoading(false)
    );

    // Mark as read when window opens
    callMarkAsRead(
      conversationId,
      () => {
        // Reset unread count for this conversation in Redux
        dispatch({ type: ACTIONS.CHAT.RESET_CONVERSATION_UNREAD, payload: conversationId });
      },
      () => {}
    );
  }, [conversationId]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Pusher real-time subscription
  useEffect(() => {
    if (!conversationId) return;
    const unsubscribe = subscribeToConversation(conversationId, (msg: ChatMessageItem) => {
      dispatch({ type: ACTIONS.CHAT.APPEND_MESSAGE, payload: { conversationId, message: msg } });
      
      // If the message is from jobseeker and we're viewing this conversation, mark as read immediately
      if (msg.sender_type === 'jobseeker') {
        callMarkAsRead(
          conversationId,
          () => {
            // markAsRead will trigger backend to broadcast updated count via Pusher
            // No need to manually update Redux here, Pusher event will handle it
          },
          () => {}
        );
      }
    });
    return unsubscribe;
  }, [conversationId]);

  // Click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const loadOlderMessages = () => {
    callGetMessages(
      conversationId,
      { limit: LIMIT, offset },
      (_msg: string, data: any) => {
        const older = data?.messages || [];
        const total = data?.total || 0;
        const current = messages;
        dispatch({
          type: ACTIONS.CHAT.SET_MESSAGES,
          payload: { conversationId, messages: [...older, ...current] },
        });
        setOffset(offset + older.length);
        setHasMore(offset + older.length < total);
      },
      () => {}
    );
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED_TYPES.includes(f.type)) {
      setFileError("Only images (JPG, PNG, GIF) and documents (PDF, DOC, DOCX) are allowed.");
      return;
    }
    if (f.size > MAX_FILE_SIZE) {
      setFileError("File size must be under 10MB.");
      return;
    }
    setFileError("");
    setFile(f);
  };

  const handleSend = () => {
    if ((!text.trim() && !file) || sending) return;
    setSending(true);
    callSendMessage(
      { conversationId, message_text: text.trim() || undefined, file: file || undefined },
      (_msg: string, data: any) => {
        if (data) {
          dispatch({ type: ACTIONS.CHAT.APPEND_MESSAGE, payload: { conversationId, message: data } });
        }
        setText("");
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        setSending(false);
      },
      () => setSending(false)
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className={`drjobs-chat-window ${compact ? "drjobs-chat-window--compact" : ""}`}>
      {/* Header */}
      <div className="drjobs-chat-window__header">
        <span className="drjobs-chat-window__title">{jobseekerName}</span>
        <div className="drjobs-chat-window__actions">
          <div className="drjobs-chat-menu" ref={menuRef}>
            <button className="drjobs-chat-menu__trigger" onClick={() => setShowMenu(!showMenu)}>
              <i className="fa fa-ellipsis-v" />
            </button>
            {showMenu && (
              <div className="drjobs-chat-menu__dropdown">
                <button
                  onClick={() => {
                    setShowMenu(false);
                    onBlockToggle();
                  }}
                >
                  {isBlocked ? (
                    <>
                      <i className="fa fa-unlock" /> Unblock this jobseeker
                    </>
                  ) : (
                    <>
                      <i className="fa fa-ban" /> Block this jobseeker
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Blocked banner */}
      {isBlocked && (
        <div className="drjobs-chat-window__blocked-banner">
          <i className="fa fa-ban" /> You have blocked this jobseeker. They cannot send messages.
        </div>
      )}

      {/* Messages */}
      <div className="drjobs-chat-window__messages">
        {loading && <div className="drjobs-chat-window__loading">Loading...</div>}
        {hasMore && !loading && (
          <button className="drjobs-chat-window__load-more" onClick={loadOlderMessages}>
            Load earlier messages
          </button>
        )}
        {messages.map((msg) => (
          <MessageBubble key={msg.message_id} message={msg} isSelf={msg.sender_type === "employer"} />
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="drjobs-chat-window__input-bar">
        {file && (
          <div className="drjobs-chat-window__file-preview">
            <i className="fa fa-paperclip" />
            <span>{file.name}</span>
            <button onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}>
              <i className="fa fa-times" />
            </button>
          </div>
        )}
        {fileError && <div className="drjobs-chat-window__file-error">{fileError}</div>}

        <div className="drjobs-chat-window__input-row">
          <button
            className="drjobs-chat-window__attach-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={isBlocked}
            title="Attach file"
          >
            <i className="fa fa-paperclip" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          <textarea
            className="drjobs-chat-window__textarea"
            placeholder={isBlocked ? "This jobseeker is blocked" : "Type a message... (Enter to send)"}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              const el = e.target;
              el.style.height = "auto";
              el.style.height = `${el.scrollHeight}px`;
            }}
            onKeyDown={handleKeyDown}
            disabled={isBlocked}
            rows={2}
          />
          <button
            className="drjobs-chat-window__send-btn"
            onClick={handleSend}
            disabled={isBlocked || sending || (!text.trim() && !file)}
          >
            {sending ? <i className="fa fa-spinner fa-spin" /> : <i className="fa fa-paper-plane" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
